import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { NotificationService } from './notification.service';
import { TranslationService } from './i18n/translation.service';
import { backgroundRequest } from './page-progress.service';

interface AppVersion {
  sha?: string;
  builtAt?: string;
}

/**
 * Polls /version.json and asks the operator to reload after a deploy.
 * The next in-app navigation reloads so a stale tab does not keep running
 * the previous build.
 */
@Injectable({ providedIn: 'root' })
export class AppVersionService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private notifications = inject(NotificationService);
  private translation = inject(TranslationService);
  private currentSha = '';
  private updatePending = false;

  start(): void {
    this.poll();
    window.setInterval(() => this.poll(), 5 * 60 * 1000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') this.poll();
    });
    this.router.events.pipe(filter((event) => event instanceof NavigationStart)).subscribe(() => {
      if (this.updatePending) window.location.reload();
    });
  }

  private poll(): void {
    const context = backgroundRequest();
    this.http.get<AppVersion>(`/version.json?t=${Date.now()}`, { context }).subscribe({
      next: (body) => {
        const sha = (body?.sha || '').trim();
        if (!sha) return;
        if (!this.currentSha) {
          this.currentSha = sha;
          return;
        }
        if (sha !== this.currentSha && !this.updatePending) {
          this.updatePending = true;
          this.notifications
            .confirm({
              title: this.translation.t('app.updateAvailable'),
              message: this.translation.t('app.updateAvailableMessage'),
              confirmText: this.translation.t('app.reload'),
              type: 'info',
            })
            .then((reload) => {
              if (reload) window.location.reload();
            });
        }
      },
      error: () => {
        // A missing version file must not interrupt the session.
      },
    });
  }
}
